import { z } from "zod";

/**
 * Curator-specific shape for `AgentInput.context`. Two run modes:
 *
 * - `autonomous` — the original three-mode discovery (web search,
 *   monitored sources, stale refresh). The Curator picks candidates on
 *   its own.
 * - `targeted` — operator hands the agent an explicit list of tool
 *   names. The Curator researches exactly those and writes drafts;
 *   discovery modes are skipped entirely.
 *
 * Used by `apps/admin-ui` (M1 #14 manual run trigger / manual tool
 * search) and parsed by `services/curator/src/run.ts` to branch the
 * prompt. The orchestrator's dispatch handler does not parse this — it
 * forwards `context` opaquely so future modes don't need a coordinated
 * orchestrator deploy.
 *
 * `toolNames` is capped at 50 to bound prompt length and run time.
 * The Curator's per-tool work is at minimum two iterations (one
 * `read_tools_and_services_filtered` + one `write_tools_draft_row`)
 * and typically more (web fetches, retries on quality-gate failures);
 * `CURATOR_MAX_ITERATIONS` (100, in `services/curator/src/run.ts`) is
 * the upper bound. The 50 cap leaves the run room to retry without
 * tripping the iteration ceiling. Raise both together if needed.
 */
export const curatorContextSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("autonomous") }),
  z.object({
    mode: z.literal("targeted"),
    toolNames: z
      .array(z.string().min(1).max(120))
      .min(1, "targeted mode requires at least one tool name")
      .max(50, "targeted mode is capped at 50 tool names per run"),
  }),
]);

export type CuratorContext = z.infer<typeof curatorContextSchema>;

/**
 * Default context when none is supplied. Pre-#14 callers (smoke harness,
 * scheduled cron) pass `{}` — preserve that as autonomous mode so they
 * keep working without coordination.
 */
export const DEFAULT_CURATOR_CONTEXT: CuratorContext = { mode: "autonomous" };

/**
 * Parse `AgentInput.context` into a typed Curator context. An empty
 * record (`{}`) is treated as `{ mode: "autonomous" }`. Anything else
 * must satisfy the discriminated union or this throws — let the caller
 * decide whether that surfaces as a 400 (orchestrator dispatch) or a
 * setup_failed error in the agent's run summary (curator).
 */
export function parseCuratorContext(raw: Record<string, unknown>): CuratorContext {
  if (Object.keys(raw).length === 0) return DEFAULT_CURATOR_CONTEXT;
  return curatorContextSchema.parse(raw);
}
